/**
 * @returns true if argument is object, null is excluded
 */
export const isObject = (item: unknown): item is Record<string | number | symbol, unknown> => {
  return item !== null && typeof item === 'object';
};

const objectConstructorSource = Function.prototype.toString.call(Object);

/**
 * @returns true only for plain objects ({} literals, new Object(), Object.create(null)),
 * same semantics as ramda-adjunct's isPlainObj: Date, RegExp, Map, arrays and class instances are excluded
 */
export const isPlainObject = (item: unknown): item is Record<string, unknown> => {
  if (!isObject(item) || Object.prototype.toString.call(item) !== '[object Object]') {
    return false;
  }

  const proto = Object.getPrototypeOf(item);
  if (proto === null) {
    return true;
  }

  const ctor = proto.constructor;
  return typeof ctor === 'function' && Function.prototype.toString.call(ctor) === objectConstructorSource;
};

/**
 * @returns true if argument is array
 */
export const isArray = (item: unknown): item is unknown[] => {
  return Array.isArray(item);
};

/**
 * @returns true if argument is string
 */
export const isString = (item: unknown): item is string => {
  return typeof item === 'string' || item instanceof String;
};

/**
 * @returns true if argument is string
 */
export const isNumber = (item: unknown): item is number => {
  return typeof item === 'number';
};

/**
 * @returns true if argument is a function
 */
export const isFunction = <T>(f: T): f is T => f instanceof Function;

/**
 * @returns true if argument is null or undefined
 */
export const isNil = (item: unknown): item is null | undefined => {
  return item === null || item === undefined || (isString(item) && item === '');
};

/**
 * @returns true if argument is null or undefined, unlike isNil an empty string is not nil
 */
export const isNullish = (item: unknown): item is null | undefined => {
  return item === null || item === undefined;
};

/**
 * @returns true if argument is undefined
 */
export const isUndefined = (item: unknown): item is undefined => {
  return item === undefined;
};

/**
 * @returns true if argument is null
 */
export const isNull = (item: unknown): item is null => {
  return item === null;
};

/**
 * @returns false if argument is null or undefined
 */
export const isNotNil = <T>(item: T | null | undefined): item is T => {
  return !isNil(item);
};

export const isNilOrEmpty = <T>(item: T | null | undefined): item is null | undefined => {
  return item === null || item === undefined || (isArray(item) && item.length === 0) || (isString(item) && item === '');
};
